/**
 * Panel "Recobros › Tareas" — la cola de emails preparados que una operadora
 * completa y envía.
 *
 * ## Por qué existe esta pantalla
 *
 * Francia no se puede automatizar del todo todavía: el importe adeudado y el
 * link de pago de Stripe **no son derivables** hasta que ms-payment absorba la
 * emisión/consulta de Stripe. La decisión de producto es partir el trabajo: el
 * motor decide A QUIÉN y CUÁNDO (respetando gates, supresiones, disputa y
 * quiet hours exactamente igual que si fuese a enviar), precarga todo lo que
 * sabe, y una persona completa lo que falta y envía.
 *
 * ## El requisito principal es la VELOCIDAD DE RECORRIDO
 *
 * Una operadora va a pasar por decenas de tareas seguidas, así que el diseño
 * se ordena entero alrededor de eso y no alrededor de "ver bonito un caso":
 *
 *   - **Maestro-detalle en la misma pantalla**: la cola a la izquierda, el
 *     formulario a la derecha. No hay navegación a otra URL para trabajar una
 *     tarea, así que no hay ni carga de página ni pérdida de los filtros.
 *   - **Foco automático en el primer campo pendiente** al abrir cada tarea: al
 *     avanzar, la operadora ya está escribiendo sin tocar el ratón.
 *   - **Avance automático a la siguiente tarea** tras enviar o descartar. La
 *     cola se recarga en segundo plano; el salto no espera a la recarga.
 *   - **Atajos de teclado**: `Ctrl/⌘+Enter` envía desde cualquier campo, `↓`/`↑`
 *     saltan de tarea. Se limitan a modificadores para no secuestrar la
 *     escritura, que es lo que se hace el 90% del tiempo aquí.
 *
 * ## Lo que la pantalla NO hace
 *
 * No reformatea lo que escribe la operadora. Los importes FR llegan a la
 * plantilla como strings ya formateados en convención francesa y el `meta.json`
 * declara explícitamente *"three separate variables — do NOT merge"*: sumar o
 * "arreglar" strings preformateados es exactamente lo que produce un
 * `45,8045,20` en una mise en demeure. El panel avisa del formato esperado
 * (`taskVariableWarning`) y envía LITERAL lo que hay en el campo.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import { Link , useSearchParams } from "react-router-dom";
import {
  createEpisodePaymentLink,
  createSuppression,
  getEpisodePaymentLinks,
  deleteSuppression,
  listSuppressions,
  dismissTask,
  getTask,
  listTasks,
  createEpisodeNote,
  deferTask,
  markTaskExternal,
  pauseEpisode,
  sendTask,
  writeOffLead,
  useMsOpsUnavailable,
} from "../../api/msops";
import { useAuth } from "../../context/AuthContext";
import RecobrosTabs from "./components/RecobrosTabs";
import {
  InvoicePaymentLink,
  MSOPS_ACTION_ERROR_HINT,
  MSOPS_MARKETS,
  msOpsErrorBody,
  PENDING_TASK_STATUS_LABEL,
  PENDING_TASK_STATUS_STYLES,
  PENDING_TASK_STATUSES,
  PROCESS_KIND_LABEL,
  TASK_VARIABLE_HELP,
  taskVariableLabel,
  taskVariableWarning,
} from "../../types/msops";
import type {
  MsOpsMarket,
  Paginated,
  PendingTaskStatus,
  ProcessKind,
  TaskDetail,
  TaskListItem,
  TaskSendResponse,
} from "../../types/msops";
import Pagination from "../OnboardingCustomers/components/Pagination";
import {
  Badge,
  buttonStyle,
  COLORS,
  Card,
  CopyableValue,
  describeError,
  EmptyRow,
  ErrorBanner,
  fieldStyle,
  fmtDateTime,
  inputStyle,
  labelStyle,
  Loading,
  secondaryButtonStyle,
  Td,
  Th,
  UnavailableNotice,
} from "./components/ui";

const DEFAULT_PAGE_SIZE = 25;

const PROCESS_KINDS: ProcessKind[] = [
  "dunning",
  "renewal",
  "signature",
  "sepa_dispute",
];

/**
 * Traduce un fallo a algo accionable: el `message` del back explica QUÉ pasó
 * (ya viene en castellano) y `MSOPS_ACTION_ERROR_HINT` añade el siguiente paso.
 * Mismo criterio que `ActionsBar`, para que un 409 se lea igual en las dos
 * pantallas del panel.
 */
type ActionError = {
  code: string | null;
  message: string;
  hint: string | null;
  /** Variables que el 422 declara como faltantes (`detail.missing`). */
  missing: string[];
  status?: number;
};

function describeTaskError(err: unknown, fallback: string): ActionError {
  const status = (err as { status?: number } | null)?.status;
  const body = msOpsErrorBody(err);
  if (body) {
    const rawMissing = body.detail?.missing;
    return {
      code: body.error,
      message: body.message || fallback,
      hint: MSOPS_ACTION_ERROR_HINT[body.error] ?? null,
      missing: Array.isArray(rawMissing)
        ? rawMissing.filter((m): m is string => typeof m === "string")
        : [],
      status,
    };
  }
  if (status === 404) {
    return {
      code: null,
      message: "Recobros no está disponible en este entorno.",
      hint: null,
      missing: [],
      status,
    };
  }
  return {
    code: null,
    message:
      typeof status === "number" ? `${fallback} (HTTP ${status})` : fallback,
    hint: null,
    missing: [],
    status,
  };
}

/**
 * ¿El error significa que la tarea ya no está en la cola? Es la carrera entre
 * operadoras (dos personas sobre la misma mise en demeure) y la única respuesta
 * útil es sacarla de la lista y seguir, no dejar a alguien reintentando algo
 * que ya salió.
 */
function isAlreadyResolved(error: ActionError): boolean {
  return (
    error.code === "tarea_no_pendiente" || error.code === "tarea_no_encontrada"
  );
}

function ActionErrorBanner({ error }: { error: ActionError }) {
  return (
    <div
      style={{
        padding: "0.6rem 0.85rem",
        background: COLORS.dangerBg,
        color: COLORS.dangerFg,
        borderRadius: "0.45rem",
        marginBottom: "0.65rem",
        fontSize: ".82rem",
        lineHeight: 1.5,
      }}
    >
      <strong>{error.message}</strong>
      {error.missing.length > 0 && (
        <div style={{ marginTop: "0.3rem" }}>
          Faltan: {error.missing.map(taskVariableLabel).join(", ")}.
        </div>
      )}
      {error.hint && <div style={{ marginTop: "0.3rem" }}>{error.hint}</div>}
    </div>
  );
}

/** Valor legible de una variable resuelta (el back las tipa como `unknown`). */
function renderVariableValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

const overdueBadgeStyle = { bg: "#fde2e2", fg: "#b32424" };

export default function Tareas() {
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [data, setData] = useState<Paginated<TaskListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // La cola nace filtrada a `pending` (como el back) porque existe para
  // vaciarse: el histórico se pide explícitamente.
  const [status, setStatus] = useState<PendingTaskStatus>("pending");
  const [market, setMarket] = useState<MsOpsMarket | "">("");
  const [processKind, setProcessKind] = useState<ProcessKind | "">("dunning");
  const [searchParams] = useSearchParams();
  /**
   * Deep-link desde la Cartera: `?episodeId=…` abre la cola ya filtrada por
   * ESE caso, en vez de dejar a la operadora buscándolo entre las páginas.
   */
  const episodeIdParam = searchParams.get("episodeId") ?? "";
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  /** Buscador por póliza o lead: el cliente llama diciendo su nº de póliza. */
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  // 0-based, como el `Pagination` del SPA. ms-ops es 1-based: se convierte en
  // la llamada.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * Ids resueltos en ESTA sesión (enviados, descartados, o que otra operadora
   * resolvió delante de nosotras). Se ocultan de la cola sin esperar a la
   * recarga: el avance a la siguiente tarea tiene que ser instantáneo.
   */
  const [resolvedIds, setResolvedIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const response = await listTasks(
        {
          page: page + 1,
          pageSize,
          status,
          market: market || undefined,
          processKind: processKind || undefined,
          overdue: onlyOverdue ? true : undefined,
          ...(qDebounced.length >= 2 ? { q: qDebounced } : {}),
          ...(episodeIdParam ? { episodeId: episodeIdParam } : {}),
        },
        { token },
      );
      setData(response);
    } catch (err) {
      setListError(describeError(err, "No se pudieron cargar las tareas"));
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, status, market, processKind, onlyOverdue, qDebounced, episodeIdParam]);

  useEffect(() => {
    const t = window.setTimeout(() => setQDebounced(q.trim()), 350);
    return () => window.clearTimeout(t);
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  // Cambiar cualquier filtro devuelve a la primera página: quedarse en la 4ª
  // de un filtro que ya no existe muestra una cola vacía que parece un fallo.
  useEffect(() => {
    setPage(0);
  }, [status, market, processKind, onlyOverdue, qDebounced, episodeIdParam]);

  /** La cola visible: lo que devolvió el back menos lo ya resuelto aquí. */
  const items = useMemo(() => {
    const all = data?.items ?? [];
    if (resolvedIds.length === 0) return all;
    return all.filter((t) => !resolvedIds.includes(t.id));
  }, [data, resolvedIds]);

  const pendingCount = useMemo(
    () => items.filter((t) => t.status === "pending").length,
    [items],
  );
  const overdueCount = useMemo(
    () => items.filter((t) => t.overdue && t.status === "pending").length,
    [items],
  );

  // Selección inicial y auto-selección: en cuanto hay cola y no hay nada
  // seleccionado, se abre la primera. La pantalla nunca está en el estado
  // "hay trabajo pero no estás haciendo ninguno".
  useEffect(() => {
    if (items.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((current) => {
      if (current && items.some((t) => t.id === current)) return current;
      return items[0].id;
    });
  }, [items]);

  const selectedIndex = useMemo(
    () => items.findIndex((t) => t.id === selectedId),
    [items, selectedId],
  );

  /**
   * Marca la tarea como resuelta y salta a la SIGUIENTE de la cola.
   *
   * El salto se calcula sobre la lista actual antes de quitar la resuelta, de
   * modo que "la siguiente" es la que la operadora tiene debajo en pantalla y
   * no lo que decida un re-render posterior. La recarga va detrás y sin await:
   * la cola se refresca sola, pero el trabajo no se detiene a esperarla.
   */
  const resolveAndAdvance = useCallback(
    (taskId: string) => {
      const index = items.findIndex((t) => t.id === taskId);
      const next = items[index + 1] ?? items[index - 1] ?? null;
      setResolvedIds((prev) =>
        prev.includes(taskId) ? prev : [...prev, taskId],
      );
      setSelectedId(next ? next.id : null);
      load();
    },
    [items, load],
  );

  const selectRelative = useCallback(
    (delta: number) => {
      if (items.length === 0) return;
      const base = selectedIndex < 0 ? 0 : selectedIndex;
      const next = Math.min(items.length - 1, Math.max(0, base + delta));
      setSelectedId(items[next].id);
    },
    [items, selectedIndex],
  );

  if (unavailable) return <UnavailableNotice />;

  return (
    <div style={{ padding: "1.25rem" }}>
      <header style={{ marginBottom: "0.9rem" }}>
        <RecobrosTabs />
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: "0.75rem",
            flexWrap: "wrap",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "1.25rem", color: COLORS.heading }}>
            Mi cola de recobros
          </h1>
          {episodeIdParam && (
            <span
              style={{
                fontSize: ".78rem",
                background: "#e7f1ff",
                color: "#0b5ed7",
                borderRadius: "999px",
                padding: ".1rem .6rem",
              }}
            >
              filtrada por un caso ·{" "}
              <Link to="/operations/recobros/tareas" style={{ color: "#0b5ed7" }}>
                ver toda la cola
              </Link>
            </span>
          )}
        </div>
        <p
          style={{
            margin: "0.35rem 0 0",
            color: COLORS.muted,
            fontSize: ".85rem",
            lineHeight: 1.5,
            maxWidth: 760,
          }}
        >
          Emails que el motor ya ha preparado y a los que les faltan datos que
          todavía no puede derivar (importes y enlaces de pago). Rellena lo
          marcado en amarillo y envía: al enviar se salta sola a la siguiente.
          Atajos: <kbd>Ctrl</kbd>+<kbd>Enter</kbd> envía, <kbd>↑</kbd>/
          <kbd>↓</kbd> cambian de tarea.
        </p>
      </header>

      <Filters
        status={status}
        onStatus={setStatus}
        market={market}
        onMarket={setMarket}
        processKind={processKind}
        onProcessKind={setProcessKind}
        onlyOverdue={onlyOverdue}
        q={q}
        onQ={setQ}
        onOnlyOverdue={setOnlyOverdue}
        pendingCount={pendingCount}
        overdueCount={overdueCount}
        total={data?.total ?? 0}
        loading={loading}
      />

      {listError && <ErrorBanner message={listError} />}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(320px, 2fr) minmax(380px, 3fr)",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <Card>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>Cliente</Th>
                  <Th>Plantilla</Th>
                  <Th>Vence</Th>
                  <Th>Faltan</Th>
                </tr>
              </thead>
              <tbody>
                {loading && items.length === 0 ? (
                  <EmptyRow colSpan={4}>Cargando tareas…</EmptyRow>
                ) : items.length === 0 ? (
                  <EmptyRow colSpan={4}>
                    No hay tareas con estos filtros. Si la cola de pendientes
                    está vacía, no queda nada por enviar.
                  </EmptyRow>
                ) : (
                  items.map((task) => (
                    <QueueRow
                      key={task.id}
                      task={task}
                      selected={task.id === selectedId}
                      onSelect={() => setSelectedId(task.id)}
                      onArrow={selectRelative}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(0);
            }}
            disabled={loading}
          />
        </Card>

        {selectedId ? (
          <TaskForm
            key={selectedId}
            taskId={selectedId}
            token={token}
            onResolved={resolveAndAdvance}
            onNavigate={selectRelative}
          />
        ) : (
          <Card style={{ padding: "1.25rem" }}>
            <div style={{ color: COLORS.muted, fontSize: ".88rem" }}>
              {items.length === 0
                ? "Cola vacía: no hay ninguna tarea que completar."
                : "Selecciona una tarea de la cola."}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filtros + contador.
// ---------------------------------------------------------------------------

function Filters({
  status,
  onStatus,
  market,
  onMarket,
  processKind,
  onProcessKind,
  onlyOverdue,
  onOnlyOverdue,
  q,
  onQ,
  pendingCount,
  overdueCount,
  total,
  loading,
}: {
  status: PendingTaskStatus;
  onStatus: (v: PendingTaskStatus) => void;
  market: MsOpsMarket | "";
  onMarket: (v: MsOpsMarket | "") => void;
  processKind: ProcessKind | "";
  onProcessKind: (v: ProcessKind | "") => void;
  onlyOverdue: boolean;
  onOnlyOverdue: (v: boolean) => void;
  q: string;
  onQ: (v: string) => void;
  pendingCount: number;
  overdueCount: number;
  total: number;
  loading: boolean;
}) {
  return (
    <Card style={{ padding: "0.75rem 0.9rem", marginBottom: "0.9rem" }}>
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <label style={{ ...fieldStyle, minWidth: "13rem" }}>
          <span style={labelStyle}>Buscar (póliza o lead)</span>
          <input
            type="search"
            style={inputStyle}
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder="GTD005537…"
          />
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Estado</span>
          <select
            style={inputStyle}
            value={status}
            onChange={(e) => onStatus(e.target.value as PendingTaskStatus)}
          >
            {PENDING_TASK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {PENDING_TASK_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Mercado</span>
          <select
            style={inputStyle}
            value={market}
            onChange={(e) => onMarket(e.target.value as MsOpsMarket | "")}
          >
            <option value="">Todos</option>
            {MSOPS_MARKETS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>

        <label style={fieldStyle}>
          <span style={labelStyle}>Proceso</span>
          <select
            style={inputStyle}
            value={processKind}
            onChange={(e) => onProcessKind(e.target.value as ProcessKind | "")}
          >
            <option value="">Todos</option>
            {PROCESS_KINDS.map((p) => (
              <option key={p} value={p}>
                {PROCESS_KIND_LABEL[p]}
              </option>
            ))}
          </select>
        </label>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.35rem",
            fontSize: ".82rem",
            color: COLORS.ink,
            paddingBottom: "0.35rem",
          }}
        >
          <input
            type="checkbox"
            checked={onlyOverdue}
            onChange={(e) => onOnlyOverdue(e.target.checked)}
          />
          Solo vencidas
        </label>

        <div style={{ marginLeft: "auto", display: "flex", gap: "0.4rem" }}>
          <Badge bg="#fff3cd" fg="#856404">
            {pendingCount} pendientes en esta página
          </Badge>
          {overdueCount > 0 && (
            <Badge bg={overdueBadgeStyle.bg} fg={overdueBadgeStyle.fg}>
              {overdueCount} vencidas
            </Badge>
          )}
          <Badge bg="#e9ecef" fg="#495057">
            {loading ? "cargando…" : `${total} en total`}
          </Badge>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Fila de la cola.
// ---------------------------------------------------------------------------

function QueueRow({
  task,
  selected,
  onSelect,
  onArrow,
}: {
  task: TaskListItem;
  selected: boolean;
  onSelect: () => void;
  onArrow: (delta: number) => void;
}) {
  const statusStyle = PENDING_TASK_STATUS_STYLES[task.status];
  return (
    <tr
      onClick={onSelect}
      tabIndex={0}
      onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          onArrow(1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          onArrow(-1);
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{
        cursor: "pointer",
        background: selected ? "#eef5ff" : "transparent",
        borderLeft: selected
          ? `3px solid ${COLORS.link}`
          : "3px solid transparent",
        borderBottom: `1px solid ${COLORS.rowBorder}`,
      }}
    >
      <Td>
        <div style={{ fontWeight: 600, fontSize: ".84rem" }}>
          {task.subscriptionRef}
        </div>
        <div style={{ color: COLORS.muted, fontSize: ".74rem" }}>
          {task.market} · {PROCESS_KIND_LABEL[task.processKind]}
        </div>
      </Td>
      <Td mono style={{ fontSize: ".74rem" }}>
        {task.templateId}
      </Td>
      <Td style={{ fontSize: ".78rem" }}>
        <div>{fmtDateTime(task.dueAt)}</div>
        {task.overdue && task.status === "pending" && (
          <Badge bg={overdueBadgeStyle.bg} fg={overdueBadgeStyle.fg}>
            Vencida
          </Badge>
        )}
      </Td>
      <Td>
        {task.status === "pending" ? (
          task.missingVariables.length > 0 ? (
            <Badge bg="#fff3cd" fg="#856404">
              {task.missingVariables.length} datos
            </Badge>
          ) : (
            <Badge bg="#d4edda" fg="#155724">
              Lista
            </Badge>
          )
        ) : (
          <Badge bg={statusStyle.bg} fg={statusStyle.fg}>
            {PENDING_TASK_STATUS_LABEL[task.status]}
          </Badge>
        )}
      </Td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// El formulario: el trabajo real.
// ---------------------------------------------------------------------------

const pendingFieldStyle: CSSProperties = {
  ...inputStyle,
  width: "100%",
  background: "#fffdf3",
  border: "1px solid #f0c36d",
};

function TaskForm({
  taskId,
  token,
  onResolved,
  onNavigate,
}: {
  taskId: string;
  token: string | null | undefined;
  onResolved: (taskId: string) => void;
  onNavigate: (delta: number) => void;
}) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ActionError | null>(null);

  const [values, setValues] = useState<Record<string, string>>({});
  /**
   * Email del destinatario, copiado por la operadora de la ficha del cliente.
   * ms-ops NO lo almacena (PII de ms-identity) y todavía no puede resolverlo
   * solo (hasta R12 de ms-payment): sin él, el back responde 422
   * `destinatario_no_resoluble` sin tocar la tarea. Pedirlo aquí es además un
   * paso más de verificación humana antes del envío real.
   */
  const [recipient, setRecipient] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [result, setResult] = useState<TaskSendResponse | null>(null);
  const [preview, setPreview] = useState<TaskSendResponse | null>(null);
  const [dismissing, setDismissing] = useState(false);
  const [dismissReason, setDismissReason] = useState("");
  /** «Aplazar»: el cliente prometió pagar otro día. */
  const [deferring, setDeferring] = useState(false);
  const [deferDate, setDeferDate] = useState("");
  const [deferReason, setDeferReason] = useState("");
  /** Nota rápida sin salir de la cola (llamadas, respuestas del cliente). */
  const [noteText, setNoteText] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  /** «Condonar deuda»: esto no merece reclamarse — perdona y cierra el caso. */
  const [condoning, setCondoning] = useState(false);
  const [condoneReason, setCondoneReason] = useState("");
  /** «Ajustar fase»: el cliente ya recibió cartas fuera de ms-ops. */
  const [adjusting, setAdjusting] = useState(false);
  const [adjustStep, setAdjustStep] = useState("");
  const [adjustDate, setAdjustDate] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustDone, setAdjustDone] = useState<string | null>(null);
  /** Se enseñan los avisos de campos vacíos sólo tras intentar enviar. */
  const [showMissing, setShowMissing] = useState(false);

  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setTask(null);
    setValues({});
    setRecipient("");
    setResult(null);
    setPreview(null);
    setActionError(null);
    setShowMissing(false);
    setDismissing(false);
    setDismissReason("");
    setDeferring(false);
    setDeferDate("");
    setDeferReason("");
    setNoteText("");
    setNoteSaved(false);
    setCondoning(false);
    setCondoneReason("");
    setAdjusting(false);
    setAdjustStep("");
    setAdjustDate("");
    setAdjustNote("");
    setAdjustDone(null);
    getTask(taskId, { token })
      .then((detail) => {
        if (!alive) return;
        setTask(detail);
        // R12: el email llega resuelto del by-lead de ms-payment — se
        // PRE-RELLENA para que la operadora lo confirme, no lo teclee. Sigue
        // siendo editable y sigue sin persistirse en ms-ops.
        setRecipient(detail.recipientEmail ?? "");
        // R8: los importes de la MED llegan sugeridos desde el balance del
        // ledger (formato FR ya listo). Editables — la operadora confirma.
        const blanks: Record<string, string> = {};
        detail.missingVariables.forEach((name) => {
          blanks[name] = detail.suggestedVariables?.[name] ?? "";
        });
        setValues(blanks);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(describeTaskError(err, "No se pudo cargar la tarea"));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [taskId, token]);

  /**
   * Foco automático en el primer campo pendiente. Es el gesto que hace que
   * recorrer la cola sea rápido: al saltar a la siguiente tarea ya se puede
   * escribir sin tocar el ratón.
   */
  useEffect(() => {
    if (!loading && task && task.missingVariables.length > 0) {
      firstFieldRef.current?.focus();
    }
  }, [loading, task]);

  const missingNow = useMemo(() => {
    if (!task) return [];
    return task.missingVariables.filter((name) => !values[name]?.trim());
  }, [task, values]);

  // Validación mínima honesta: el formato fino lo valida el back (zod .email()).
  const recipientOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient.trim());

  const canSend =
    !!task &&
    task.status === "pending" &&
    missingNow.length === 0 &&
    recipientOk &&
    !busy;

  const doSend = useCallback(
    async (dryRun: boolean) => {
      if (!task) return;
      if (missingNow.length > 0 || (!dryRun && !recipientOk)) {
        setShowMissing(true);
        return;
      }
      setBusy(true);
      setActionError(null);
      try {
        // Se envía LITERAL lo que hay en el campo: el contrato pide strings ya
        // formateados y "arreglarlos" aquí es lo que rompe la plantilla.
        const trimmed: Record<string, string> = {};
        Object.entries(values).forEach(([k, v]) => {
          trimmed[k] = v.trim();
        });
        const response = await sendTask(
          task.id,
          {
            variables: trimmed,
            dryRun: dryRun || undefined,
            // También en dryRun: así la previsualización enseña el buzón real.
            ...(recipient.trim() !== "" ? { recipientEmail: recipient.trim() } : {}),
          },
          { token },
        );
        if (dryRun) {
          setPreview(response);
          setBusy(false);
          return;
        }
        setResult(response);
        setBusy(false);
        // `simulated: true` con 200 = gate del mercado cerrado: NO ha salido
        // nada y la tarea sigue pendiente. Sería un engaño saltar a la
        // siguiente como si el cliente hubiera recibido el email.
        if (!response.simulated) {
          window.setTimeout(() => onResolved(task.id), 700);
        }
      } catch (err) {
        const error = describeTaskError(err, "No se pudo enviar la tarea");
        setActionError(error);
        setBusy(false);
        // Carrera entre operadoras: si otra persona ya la resolvió, sacarla de
        // la cola y avanzar es lo único útil — reintentar no la va a devolver.
        if (isAlreadyResolved(error)) {
          window.setTimeout(() => onResolved(task.id), 1600);
        }
      }
    },
    [task, values, recipient, missingNow, token, onResolved],
  );

  const doDismiss = useCallback(async () => {
    if (!task || dismissReason.trim() === "") return;
    setBusy(true);
    setActionError(null);
    try {
      await dismissTask(task.id, { motivo: dismissReason.trim() }, { token });
      setBusy(false);
      onResolved(task.id);
    } catch (err) {
      const error = describeTaskError(err, "No se pudo descartar la tarea");
      setActionError(error);
      setBusy(false);
      if (isAlreadyResolved(error)) {
        window.setTimeout(() => onResolved(task.id), 1600);
      }
    }
  }, [task, dismissReason, token, onResolved]);

  const doAdjust = useCallback(async () => {
    if (!task || adjustStep === "" || adjustDate === "") return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await markTaskExternal(
        task.id,
        {
          ultimoPasoEnviado: adjustStep,
          fechaEnvio: adjustDate,
          ...(adjustNote.trim() !== "" ? { nota: adjustNote.trim() } : {}),
        },
        { token },
      );
      setBusy(false);
      setAdjusting(false);
      setAdjustDone(
        `Fase ajustada: ${res.stepsMarked.length} paso(s) registrados como ya enviados. ` +
          "El siguiente escalón de este cliente aparecerá en la cola en la próxima pasada del motor (~10 min), con su plazo contado desde la fecha real.",
      );
      window.setTimeout(() => onResolved(task.id), 2500);
    } catch (err) {
      const error = describeTaskError(err, "No se pudo ajustar la fase");
      setActionError(error);
      setBusy(false);
      if (isAlreadyResolved(error)) {
        window.setTimeout(() => onResolved(task.id), 1600);
      }
    }
  }, [task, adjustStep, adjustDate, adjustNote, token, onResolved]);

  /**
   * Aplaza la tarea: el cliente ha llamado y pagará otro día. La tarea sigue
   * PENDIENTE — al contrario que descartar, que da el paso por resuelto y hace
   * que esa carta no salga nunca.
   */
  const doDefer = useCallback(async () => {
    if (!task || deferDate === "" || deferReason.trim().length < 3) return;
    setBusy(true);
    setActionError(null);
    try {
      await deferTask(
        task.id,
        { hasta: deferDate, motivo: deferReason.trim() },
        { token },
      );
      setBusy(false);
      setDeferring(false);
      onResolved(task.id);
    } catch (err) {
      setActionError(describeTaskError(err, "No se pudo aplazar la tarea"));
      setBusy(false);
    }
  }, [task, deferDate, deferReason, token, onResolved]);

  /**
   * «Prometió pagar»: aplaza la tarea Y pausa el episodio hasta esa fecha, de
   * modo que la cadencia entera se congela (no solo este paso) y el caso sale
   * de la cola hasta el día prometido.
   */
  const doPromise = useCallback(async () => {
    if (!task || deferDate === "" || deferReason.trim().length < 3) return;
    setBusy(true);
    setActionError(null);
    try {
      await deferTask(task.id, { hasta: deferDate, motivo: deferReason.trim() }, { token });
      await pauseEpisode(
        task.episodeId,
        {
          motivo: `promesa de pago (${deferDate}): ${deferReason.trim()}`,
          // La supresión caduca sola el día prometido: si el cliente no paga,
          // la cadencia se reanuda sin que nadie tenga que acordarse.
          hasta: `${deferDate}T09:00:00.000Z`,
        },
        { token },
      );
      setBusy(false);
      setDeferring(false);
      onResolved(task.id);
    } catch (err) {
      setActionError(describeTaskError(err, "No se pudo registrar la promesa de pago"));
      setBusy(false);
    }
  }, [task, deferDate, deferReason, token, onResolved]);

  /** Nota rápida en el episodio, sin salir de la cola. */
  const doNote = useCallback(async () => {
    if (!task || noteText.trim().length < 2) return;
    setBusy(true);
    try {
      await createEpisodeNote(task.episodeId, { body: noteText.trim() }, { token });
      setNoteText("");
      setNoteSaved(true);
      window.setTimeout(() => setNoteSaved(false), 2500);
    } catch (err) {
      setActionError(describeTaskError(err, "No se pudo guardar la nota"));
    } finally {
      setBusy(false);
    }
  }, [task, noteText, token]);

  /**
   * Condona la deuda de este cliente y CIERRA el caso: descarta esta tarea con
   * el motivo, cierra el episodio como `written_off` y perdona los recibos
   * vigentes en el ledger de ms-payment (con el nombre de quien lo decide).
   * Es la salida para "reclamar esto cuesta más de lo que se recupera" —
   * hasta hoy solo existía descartar la tarea, que dejaba la deuda viva y el
   * episodio congelado.
   */
  const doCondone = useCallback(async () => {
    if (!task || !task.leadUlid || condoneReason.trim().length < 5) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await writeOffLead(
        task.leadUlid,
        task.market,
        { motivo: condoneReason.trim(), cerrarEpisodio: true, condonarPolizaViva: true },
        { token },
      );
      setBusy(false);
      setCondoning(false);
      window.alert(
        `Condonados ${res.receiptsWrittenOff} recibo(s) · ${(res.centsWrittenOff / 100)
          .toFixed(2)
          .replace(".", ",")} €. El caso queda cerrado.` +
          (res.receiptsFailed.length > 0
            ? ` ⚠ ${res.receiptsFailed.length} recibo(s) los rechazó ms-payment: avisa a tecnología.`
            : ""),
      );
      onResolved(task.id);
    } catch (err) {
      setActionError(describeTaskError(err, "No se pudo condonar la deuda"));
      setBusy(false);
    }
  }, [task, condoneReason, token, onResolved]);

  /**
   * Atajos. Se exigen modificadores (o teclas que no producen texto) porque
   * este formulario se rellena escribiendo: un atajo con letra suelta
   * secuestraría lo que se está tecleando en un importe.
   */
  const onFormKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (canSend) doSend(false);
      else setShowMissing(true);
      return;
    }
    if ((e.altKey || e.metaKey) && e.key === "ArrowDown") {
      e.preventDefault();
      onNavigate(1);
    } else if ((e.altKey || e.metaKey) && e.key === "ArrowUp") {
      e.preventDefault();
      onNavigate(-1);
    }
  };

  if (loading) return <Card><Loading label="Cargando tarea…" /></Card>;

  if (loadError) {
    return (
      <Card style={{ padding: "1rem" }}>
        <ActionErrorBanner error={loadError} />
      </Card>
    );
  }

  if (!task) return null;

  const resolvedEntries = Object.entries(task.resolvedVariables);
  const blocked = task.episodeStatus !== "open" || task.episodeDisputed;

  return (
    <Card style={{ padding: "1rem" }} >
      <div onKeyDown={onFormKeyDown}>
        <TaskHeader task={task} token={token} />

        {blocked && <EpisodeGuard task={task} />}

        {actionError && <ActionErrorBanner error={actionError} />}

        {result && <SendResult result={result} />}

        {task.status !== "pending" && (
          <div
            style={{
              padding: "0.6rem 0.85rem",
              background: "#e9ecef",
              borderRadius: "0.45rem",
              marginBottom: "0.7rem",
              fontSize: ".82rem",
              color: COLORS.ink,
            }}
          >
            Esta tarea ya está {PENDING_TASK_STATUS_LABEL[task.status].toLowerCase()}
            {task.completedBy ? ` por ${task.completedBy}` : ""}
            {task.completedAt ? ` el ${fmtDateTime(task.completedAt)}` : ""}.
            {task.dismissReason && ` Motivo: ${task.dismissReason}`}
          </div>
        )}

        {/* --- Deuda ACTUAL según ms-payment (R8, al vuelo) -------------- */}
        {task.balance && (
          <div
            style={{
              marginBottom: "0.9rem",
              padding: "0.55rem 0.75rem",
              borderRadius: "0.45rem",
              fontSize: ".82rem",
              ...(task.balance.alDia
                ? { background: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb" }
                : { background: "#eef4fb", color: COLORS.ink, border: `1px solid ${COLORS.border}` }),
            }}
          >
            {task.balance.alDia ? (
              <>
                <strong>⚠ ms-payment dice que este cliente está AL DÍA</strong> (sin
                recibos pendientes ni disputas). Verifica en Stripe antes de enviar
                una carta de impago — puede que haya pagado hace un momento.
              </>
            ) : (
              <>
                <strong>Deuda actual:</strong>{" "}
                {(task.balance.totalDueCents / 100).toFixed(2).replace(".", ",")} € en{" "}
                {task.balance.recibosPendientes} recibo(s)
                {task.balance.collectionFeesCents > 0 &&
                  ` · gastos ${(task.balance.collectionFeesCents / 100).toFixed(2).replace(".", ",")} €`}
                {task.balance.chargebackCents > 0 &&
                  ` · chargeback ${(task.balance.chargebackCents / 100).toFixed(2).replace(".", ",")} €`}
                {task.balance.disputasVivas > 0 &&
                  ` · ⚠ ${task.balance.disputasVivas} disputa(s) viva(s)`}
                {task.balance.deudaDesde &&
                  ` · desde ${task.balance.deudaDesde.slice(0, 10)}`}
                {" — los importes de abajo vienen pre-rellenados con estas cifras."}
              </>
            )}
          </div>
        )}

        {/* P3: con qué está fallando el cobro. Va fuera de la banda de deuda
            porque se muestra igual cuando el balance no se pudo resolver — es
            el dato que la operadora necesita si coge el teléfono. */}
        {task.paymentMethod && (
          <div
            style={{
              margin: "0 0 .7rem",
              fontSize: ".8rem",
              color: COLORS.muted,
            }}
          >
            <strong>Método de pago:</strong>{" "}
            {task.paymentMethod.type === "card"
              ? task.paymentMethod.last4
                ? `tarjeta acabada en ${task.paymentMethod.last4}`
                : "tarjeta"
              : task.paymentMethod.type === "sepa_debit"
                ? "domiciliación SEPA"
                : task.paymentMethod.type}
          </div>
        )}

        {/* --- Lo que ya sabe el motor: sólo lectura --------------------- */}
        <section style={{ marginBottom: "0.9rem" }}>
          <h3 style={sectionTitleStyle}>Datos ya resueltos</h3>
          {resolvedEntries.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: ".82rem" }}>
              El motor no resolvió ninguna variable para esta plantilla.
            </div>
          ) : (
            <dl style={{ margin: 0, display: "grid", gap: "0.35rem" }}>
              {resolvedEntries.map(([name, value]) => (
                <div
                  key={name}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(140px, 1fr) 2fr",
                    gap: "0.5rem",
                    alignItems: "baseline",
                  }}
                >
                  <dt style={{ ...labelStyle, margin: 0 }}>
                    {taskVariableLabel(name)}
                  </dt>
                  <dd
                    style={{
                      margin: 0,
                      fontSize: ".85rem",
                      color: COLORS.ink,
                      wordBreak: "break-word",
                    }}
                  >
                    {renderVariableValue(value)}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        {/* --- Lo que rellena la operadora ------------------------------ */}
        <section style={{ marginBottom: "0.9rem" }}>
          <h3 style={sectionTitleStyle}>
            Datos que faltan{" "}
            {task.missingVariables.length > 0 && (
              <Badge bg="#fff3cd" fg="#856404">
                {task.missingVariables.length}
              </Badge>
            )}
          </h3>
          {task.missingVariables.length === 0 ? (
            <div style={{ color: COLORS.muted, fontSize: ".82rem" }}>
              Nada que rellenar: esta plantilla no declara variables pendientes.
              Puedes enviarla directamente.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "0.7rem" }}>
              {task.missingVariables.map((name, index) => (
                <Fragment key={name}>
                  <PendingField
                    name={name}
                    market={task.market}
                    value={values[name] ?? ""}
                    onChange={(v) =>
                      setValues((prev) => ({ ...prev, [name]: v }))
                    }
                    inputRef={index === 0 ? firstFieldRef : undefined}
                    showMissing={showMissing}
                    disabled={busy || task.status !== "pending"}
                  />
                  {name === "payment_link" && (
                    <PaymentLinkPicker
                      episodeId={task.episodeId}
                      token={token}
                      disabled={busy || task.status !== "pending"}
                      onPick={(url) =>
                        setValues((prev) => ({ ...prev, payment_link: url }))
                      }
                    />
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </section>

        {/* --- Destinatario --------------------------------------------- */}
        <section style={{ marginBottom: "0.9rem" }}>
          <h3 style={sectionTitleStyle}>Destinatario</h3>
          <label style={{ display: "grid", gap: "0.25rem" }}>
            <span style={labelStyle}>Email del cliente</span>
            <input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="cliente@ejemplo.fr"
              disabled={busy || task.status !== "pending"}
              style={{
                ...inputStyle,
                ...(showMissing && !recipientOk
                  ? { borderColor: "#dc3545", background: "#fff5f5" }
                  : {}),
              }}
            />
            <span style={{ color: COLORS.muted, fontSize: ".75rem" }}>
              Cópialo de la ficha del cliente (enlace «Validar datos» de arriba)
              y comprueba que es la persona correcta. No se guarda en ms-ops:
              se usa solo para este envío.
            </span>
          </label>
        </section>

        {/* --- Previsualización ---------------------------------------- */}
        {preview && <PreviewPanel response={preview} />}

        {/* --- Nota rápida (llamadas, respuestas del cliente) ------------ */}
        <section style={{ marginBottom: "0.9rem" }}>
          <h3 style={sectionTitleStyle}>Anotar</h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
            <input
              type="text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="p.ej. llamada 10/08: dice que pagó por transferencia el viernes"
              disabled={busy}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              type="button"
              style={{ ...secondaryButtonStyle, opacity: noteText.trim().length >= 2 ? 1 : 0.5 }}
              disabled={noteText.trim().length < 2 || busy}
              onClick={() => void doNote()}
            >
              Guardar nota
            </button>
          </div>
          <div style={{ color: noteSaved ? "#155724" : COLORS.muted, fontSize: ".75rem", marginTop: ".25rem" }}>
            {noteSaved
              ? "✓ Nota guardada en el episodio."
              : "Queda en el caso de recobro, con tu nombre y la fecha (todavía no en el historial general del cliente)."}
          </div>
        </section>

        {/* --- Acciones ------------------------------------------------- */}
        {showMissing && (missingNow.length > 0 || !recipientOk) && (
          <div
            style={{
              padding: "0.5rem 0.7rem",
              background: "#fff3cd",
              color: "#856404",
              borderRadius: "0.45rem",
              marginBottom: "0.6rem",
              fontSize: ".8rem",
            }}
          >
            Para enviar hay que rellenar:{" "}
            {[
              ...missingNow.map(taskVariableLabel),
              ...(recipientOk ? [] : ["el email del destinatario"]),
            ].join(", ")}
            . El envío es todo o nada: un importe sin sustituir en el email es
            peor que no mandarlo.
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            style={{
              ...buttonStyle,
              opacity: canSend ? 1 : 0.5,
              cursor: canSend ? "pointer" : "not-allowed",
            }}
            disabled={!canSend}
            onClick={() => doSend(false)}
            title={
              canSend
                ? "Enviar (Ctrl+Enter)"
                : "Rellena todos los campos pendientes para poder enviar"
            }
          >
            {busy ? "Enviando…" : "Enviar"}
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            disabled={busy || task.status !== "pending"}
            onClick={() => doSend(true)}
            title="Valida y muestra el email exacto que saldría, sin enviarlo"
          >
            Previsualizar
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            disabled={busy || task.status !== "pending"}
            onClick={() => setDismissing((v) => !v)}
          >
            Descartar
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            disabled={busy || task.status !== "pending" || task.cadenceSteps.length === 0}
            onClick={() => setAdjusting((v) => !v)}
            title="El cliente ya recibió esta carta (u otra posterior) por Gmail antes de arrancar ms-ops"
          >
            Ya se envió fuera…
          </button>
          <button
            type="button"
            style={secondaryButtonStyle}
            disabled={busy || task.status !== "pending"}
            onClick={() => setDeferring((v) => !v)}
            title="El cliente ha llamado y pagará otro día: mueve la tarea sin saltarse la carta"
          >
            Aplazar…
          </button>
          <button
            type="button"
            style={{ ...secondaryButtonStyle, borderColor: "#856404", color: "#856404" }}
            disabled={busy || task.status !== "pending" || !task.leadUlid}
            onClick={() => setCondoning((v) => !v)}
            title="Perdonar la deuda y cerrar el caso: para cuando reclamar cuesta más de lo que se recupera"
          >
            Condonar deuda…
          </button>
          <button
            type="button"
            style={{ ...secondaryButtonStyle, marginLeft: "auto" }}
            onClick={() => onNavigate(1)}
            title="Siguiente tarea (Alt+↓)"
          >
            Saltar →
          </button>
        </div>

        {adjustDone && (
          <div
            style={{
              marginTop: "0.6rem",
              padding: "0.5rem 0.7rem",
              background: "#d4edda",
              color: "#155724",
              borderRadius: "0.45rem",
              fontSize: ".8rem",
            }}
          >
            {adjustDone}
          </div>
        )}

        {deferring && (
          <div
            style={{
              marginTop: "0.6rem",
              padding: "0.7rem",
              background: "#f6f8fa",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "0.45rem",
              display: "grid",
              gap: "0.55rem",
            }}
          >
            <div style={{ fontSize: ".82rem", color: COLORS.ink }}>
              <strong>Aplazar esta tarea.</strong> La carta NO se envía hoy y la tarea
              vuelve a la cola el día que elijas — el paso sigue pendiente, así que la
              cadencia no se salta nada (a diferencia de descartar).
            </div>
            <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
              <label style={{ display: "grid", gap: "0.25rem" }}>
                <span style={labelStyle}>Volver a verla el</span>
                <input
                  type="date"
                  value={deferDate}
                  onChange={(e) => setDeferDate(e.target.value)}
                  disabled={busy}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: "grid", gap: "0.25rem", flex: 1, minWidth: "14rem" }}>
                <span style={labelStyle}>Motivo</span>
                <input
                  type="text"
                  value={deferReason}
                  onChange={(e) => setDeferReason(e.target.value)}
                  placeholder="p.ej. llamó y paga el viernes"
                  disabled={busy}
                  style={inputStyle}
                />
              </label>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                type="button"
                style={{
                  ...buttonStyle,
                  opacity: deferDate && deferReason.trim().length >= 3 && !busy ? 1 : 0.5,
                }}
                disabled={!deferDate || deferReason.trim().length < 3 || busy}
                onClick={() => void doDefer()}
              >
                {busy ? "Aplazando…" : "Aplazar la tarea"}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={!deferDate || deferReason.trim().length < 3 || busy}
                onClick={() => void doPromise()}
                title="Además de aplazar, congela TODA la cadencia de este cliente hasta esa fecha"
              >
                Promesa de pago (congela el caso)
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={busy}
                onClick={() => setDeferring(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {condoning && (
          <div
            style={{
              marginTop: "0.6rem",
              padding: "0.7rem",
              background: "#fffaf0",
              border: "1px solid #f0c36d",
              borderRadius: "0.45rem",
              display: "grid",
              gap: "0.55rem",
            }}
          >
            <div style={{ fontSize: ".82rem", color: COLORS.ink }}>
              <strong>Condonar la deuda de este cliente.</strong> Se perdonan sus recibos
              vigentes
              {task.balance && task.balance.totalDueCents > 0
                ? ` (${(task.balance.totalDueCents / 100).toFixed(2).replace(".", ",")} €)`
                : ""}
              , se descarta esta tarea y se cierra el caso. Queda en el ledger de ms-payment
              con tu nombre y el motivo — <strong>no es reversible desde aquí</strong>. Úsalo
              cuando reclamar cueste más de lo que se recupera; si el cliente solo necesita
              tiempo, mejor pausar el episodio.
            </div>
            <label style={{ display: "grid", gap: "0.25rem" }}>
              <span style={labelStyle}>Motivo (obligatorio)</span>
              <input
                type="text"
                value={condoneReason}
                onChange={(e) => setCondoneReason(e.target.value)}
                placeholder="p.ej. 3,20 € de 2023, no compensa reclamar"
                disabled={busy}
                style={inputStyle}
              />
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                type="button"
                style={{
                  ...buttonStyle,
                  background: "#856404",
                  borderColor: "#856404",
                  opacity: condoneReason.trim().length >= 5 && !busy ? 1 : 0.5,
                }}
                disabled={condoneReason.trim().length < 5 || busy}
                onClick={() => void doCondone()}
              >
                {busy ? "Condonando…" : "Condonar y cerrar el caso"}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={busy}
                onClick={() => setCondoning(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {adjusting && (
          <div
            style={{
              marginTop: "0.6rem",
              padding: "0.7rem",
              background: "#f6f8fa",
              border: `1px solid ${COLORS.border}`,
              borderRadius: "0.45rem",
              display: "grid",
              gap: "0.55rem",
            }}
          >
            <div style={{ fontSize: ".8rem", color: COLORS.ink }}>
              <strong>Ajustar fase.</strong> Para clientes que YA estaban en
              proceso antes de ms-ops: indica la última carta que recibió (mira
              el Gmail de soporte o la etapa del ticket en HubSpot) y su fecha.
              Los pasos anteriores quedarán registrados como hechos —con tu
              nombre en la traza— y los plazos siguientes se contarán desde esa
              fecha real.
            </div>
            <label style={{ display: "grid", gap: "0.25rem" }}>
              <span style={labelStyle}>Última carta que recibió el cliente</span>
              <select
                value={adjustStep}
                onChange={(e) => setAdjustStep(e.target.value)}
                disabled={busy}
                style={inputStyle}
              >
                <option value="">— elige la carta —</option>
                {task.cadenceSteps.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.position}. {st.templateId ?? st.id}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "grid", gap: "0.25rem" }}>
              <span style={labelStyle}>Fecha en que se envió</span>
              <input
                type="date"
                value={adjustDate}
                onChange={(e) => setAdjustDate(e.target.value)}
                disabled={busy}
                style={inputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: "0.25rem" }}>
              <span style={labelStyle}>Nota (opcional)</span>
              <input
                type="text"
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                placeholder="p.ej. MED por Gmail 15/06, ticket HubSpot"
                disabled={busy}
                style={inputStyle}
              />
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                type="button"
                style={{
                  ...buttonStyle,
                  opacity: adjustStep && adjustDate && !busy ? 1 : 0.5,
                }}
                disabled={!adjustStep || !adjustDate || busy}
                onClick={doAdjust}
              >
                {busy ? "Ajustando…" : "Registrar y ajustar"}
              </button>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={busy}
                onClick={() => setAdjusting(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {dismissing && (
          <DismissBox
            reason={dismissReason}
            onReason={setDismissReason}
            onCancel={() => setDismissing(false)}
            onConfirm={doDismiss}
            busy={busy}
          />
        )}
      </div>
    </Card>
  );
}

const sectionTitleStyle: CSSProperties = {
  margin: "0 0 0.5rem",
  fontSize: ".78rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".4px",
  color: COLORS.muted,
};

/**
 * El switch "auto-recobro" por cliente (idea de Yago, 2026-08-08): default ON
 * para todo el mundo — sin supresión, el motor reintenta el cobro (R6) antes de
 * cada mensaje. Ops lo apaga para casos concretos (supresión kind='auto_retry')
 * y esos casos viven además en la pantalla de control /recobros/auto-recobro.
 * Independiente del Pausado clásico: apagar el cobro NO apaga los mensajes.
 */
function AutoRetryToggle({ leadUlid, token }: { leadUlid: string; token: string | null | undefined }) {
  const [state, setState] = useState<"on" | "off" | "cargando">("cargando");
  const [suppressionId, setSuppressionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setState("cargando");
    listSuppressions({ q: leadUlid, kind: "auto_retry", active: true, pageSize: 1 }, { token })
      .then((res) => {
        if (!alive) return;
        const hit = res.items.find((sup) => sup.leadUlid === leadUlid) ?? null;
        setSuppressionId(hit?.id ?? null);
        setState(hit ? "off" : "on");
      })
      .catch(() => alive && setState("on"));
    return () => {
      alive = false;
    };
  }, [leadUlid, token]);

  const turnOff = useCallback(async () => {
    const motivo = window.prompt(
      "Apagar el auto-recobro para este cliente (los mensajes SIGUEN saliendo).\nMotivo (obligatorio):",
    );
    if (!motivo?.trim()) return;
    setBusy(true);
    try {
      const created = await createSuppression(
        { leadUlid, kind: "auto_retry", reason: motivo.trim() },
        { token },
      );
      setSuppressionId(created.id);
      setState("off");
    } catch (err) {
      window.alert(`No se pudo apagar: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [leadUlid, token]);

  const turnOn = useCallback(async () => {
    if (!suppressionId) return;
    setBusy(true);
    try {
      await deleteSuppression(suppressionId, { token });
      setSuppressionId(null);
      setState("on");
    } catch (err) {
      window.alert(`No se pudo reactivar: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }, [suppressionId, token]);

  if (state === "cargando") return null;
  return state === "on" ? (
    <button
      type="button"
      onClick={turnOff}
      disabled={busy}
      title="El motor reintenta el cobro automáticamente antes de cada mensaje (R6). Apágalo solo para este cliente; los mensajes siguen su calendario."
      style={{ ...linkishButtonStyle }}
    >
      Auto-recobro: ON
    </button>
  ) : (
    <span style={{ display: "inline-flex", gap: "0.35rem", alignItems: "center" }}>
      <Badge bg="#f8d7da" fg="#842029">
        Auto-recobro OFF
      </Badge>
      <button type="button" onClick={turnOn} disabled={busy} style={linkishButtonStyle}>
        reactivar
      </button>
    </span>
  );
}

const linkishButtonStyle: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: COLORS.link,
  cursor: "pointer",
  fontSize: "inherit",
  textDecoration: "underline",
};

function TaskHeader({ task, token }: { task: TaskDetail; token: string | null | undefined }) {
  return (
    <div style={{ marginBottom: "0.8rem" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.05rem", color: COLORS.heading }}>
          {task.subscriptionRef}
        </h2>
        <Badge bg="#e9ecef" fg="#495057">
          {task.market}
        </Badge>
        <Badge
          bg={PENDING_TASK_STATUS_STYLES[task.status].bg}
          fg={PENDING_TASK_STATUS_STYLES[task.status].fg}
        >
          {PENDING_TASK_STATUS_LABEL[task.status]}
        </Badge>
        {task.overdue && task.status === "pending" && (
          <Badge bg={overdueBadgeStyle.bg} fg={overdueBadgeStyle.fg}>
            Vencida {fmtDateTime(task.dueAt)}
          </Badge>
        )}
      </div>
      <div
        style={{
          marginTop: "0.3rem",
          fontSize: ".78rem",
          color: COLORS.muted,
          display: "flex",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        <span>
          Plantilla: <code>{task.templateId}</code>
        </span>
        <span>Responsable: {task.assignee}</span>
        <span>Vence: {fmtDateTime(task.dueAt)}</span>
        <Link
          to={`/operations/recobros/${task.episodeId}`}
          style={{ color: COLORS.link }}
        >
          Ver episodio
        </Link>
        {task.leadUlid && (
          <Link
            to={`/leads/${task.leadUlid}`}
            target="_blank"
            rel="noreferrer"
            style={{ color: COLORS.link }}
          >
            Ver cliente ↗
          </Link>
        )}
        {task.leadUlid && <AutoRetryToggle leadUlid={task.leadUlid} token={token} />}
        {task.stripe?.accountId &&
          task.stripe.invoiceIds.map((invoiceId) => (
            <a
              key={invoiceId}
              href={`https://dashboard.stripe.com/${task.stripe.accountId}/invoices/${invoiceId}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: COLORS.link }}
            >
              Stripe: {task.stripe.invoiceIds.length > 1 ? shortInvoiceId(invoiceId) : "factura"} ↗
            </a>
          ))}
      </div>
    </div>
  );
}

/** `in_1RxAbCdEfGh…` → `in_…dEfGh` — suficiente para distinguirlas en la fila. */
function shortInvoiceId(id: string): string {
  return id.length > 10 ? `…${id.slice(-6)}` : id;
}

/**
 * La señal que evita el peor fallo de esta cadencia: mandar una resiliación a
 * alguien que pagó ayer. El back lo rechaza con 409, pero eso es la red; esto
 * está en el mismo formulario donde se pulsa Enviar.
 */
function EpisodeGuard({ task }: { task: TaskDetail }) {
  const closed = task.episodeStatus === "closed";
  return (
    <div
      style={{
        padding: "0.6rem 0.85rem",
        background: COLORS.dangerBg,
        color: COLORS.dangerFg,
        borderRadius: "0.45rem",
        marginBottom: "0.7rem",
        fontSize: ".82rem",
        lineHeight: 1.5,
      }}
    >
      <strong>
        {closed
          ? "El episodio está CERRADO: el impago ya se resolvió."
          : task.episodeDisputed
            ? "El episodio está EN DISPUTA."
            : "El episodio está PAUSADO."}
      </strong>{" "}
      {closed
        ? "No se comunica sobre una deuda saldada — probablemente el cliente ya ha pagado. Descarta la tarea indicando el motivo."
        : "La cadencia debería estar congelada. Comprueba el episodio antes de enviar nada."}{" "}
      El envío será rechazado por ms-ops.
    </div>
  );
}

function PendingField({
  name,
  market,
  value,
  onChange,
  inputRef,
  showMissing,
  disabled,
}: {
  name: string;
  market: MsOpsMarket;
  value: string;
  onChange: (v: string) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  showMissing: boolean;
  disabled: boolean;
}) {
  const meta = TASK_VARIABLE_HELP[name];
  const warning = taskVariableWarning(name, value, market);
  const empty = value.trim() === "";
  return (
    <label style={fieldStyle}>
      <span style={{ ...labelStyle, color: COLORS.ink }}>
        {meta?.label ?? name}{" "}
        <code style={{ color: COLORS.muted, fontWeight: 400 }}>{name}</code>
      </span>
      <input
        ref={inputRef}
        value={value}
        disabled={disabled}
        placeholder={meta?.placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...pendingFieldStyle,
          borderColor: showMissing && empty ? "#b32424" : "#f0c36d",
        }}
      />
      {meta?.help && (
        <span style={{ fontSize: ".72rem", color: COLORS.muted, lineHeight: 1.4 }}>
          {meta.help}
        </span>
      )}
      {warning && (
        <span style={{ fontSize: ".72rem", color: "#856404", lineHeight: 1.4 }}>
          ⚠ {warning}
        </span>
      )}
    </label>
  );
}

/**
 * Selector de enlace de pago — la respuesta a "¿qué link pongo si hay varios
 * recibos?" (petición de Yago, 2026-08-08).
 *
 * El hueco `payment_link` solo queda abierto cuando el cliente debe MÁS de un
 * recibo (con uno, el binding lo rellena solo): este bloque carga bajo demanda
 * los links reales por recibo desde ms-ops (que los resuelve contra ms-payment
 * en caliente) y un clic pone el elegido en el campo. Decisiones deliberadas:
 *
 *   - **El más antiguo va marcado "recomendado"** (ms-payment los devuelve en
 *     orden cronológico): en recobro se reclama primero la deuda más vieja, y
 *     es además el criterio candidato para `payment_link_label` que Ops tiene
 *     pendiente de cerrar.
 *   - **Nada se auto-rellena sin clic**: elegir UN recibo entre varios es una
 *     decisión de cobro, no un dato derivable — mismo principio fail-closed
 *     que el resto del formulario.
 *   - **"Un link por el TOTAL" aparece deshabilitado**: es la pieza 2 de P2.b
 *     de ms-payment (link de importe arbitrario). Enseñarlo apagado documenta
 *     el camino sin fingir que existe.
 *   - **Los recibos DISPUTADOS no se listan** (ms-ops ya los excluye): invitar
 *     a pagar un chargeback por su link viejo va contra la política del 7-ago
 *     (confirmar con el cliente → reemitir). Ese flujo tendrá su propio botón.
 */
function PaymentLinkPicker({
  episodeId,
  token,
  disabled,
  onPick,
}: {
  episodeId: string;
  token: string | null | undefined;
  disabled: boolean;
  onPick: (url: string) => void;
}) {
  const [links, setLinks] = useState<InvoicePaymentLink[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createLink = useCallback(
    async (invoiceId: string) => {
      setCreating(invoiceId);
      setError(null);
      try {
        const res = await createEpisodePaymentLink(episodeId, invoiceId, { token });
        // La fila pasa a tener link y queda lista para "Usar este"; se rellena
        // directamente además: quien pulsó Crear quería ESTE link en el campo.
        setLinks((prev) =>
          prev
            ? prev.map((l) =>
                l.invoiceId === invoiceId
                  ? { ...l, paymentUrl: res.paymentUrl, error: null }
                  : l,
              )
            : prev,
        );
        onPick(res.paymentUrl);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setCreating(null);
      }
    },
    [episodeId, token, onPick],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getEpisodePaymentLinks(episodeId, { token });
      setLinks(res.links);
      if (res.links.length === 0) {
        setError(
          "ms-payment no lista recibos cobrables para este episodio (¿ya pagado o en disputa?).",
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [episodeId, token]);

  return (
    <div
      style={{
        border: `1px dashed ${COLORS.border}`,
        borderRadius: "0.45rem",
        padding: "0.55rem 0.7rem",
        fontSize: ".78rem",
        display: "grid",
        gap: "0.45rem",
      }}
    >
      {links === null ? (
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <button
            type="button"
            onClick={load}
            disabled={disabled || loading}
            style={secondaryButtonStyle}
          >
            {loading ? "Cargando recibos…" : "Elegir de los recibos del cliente"}
          </button>
          <span style={{ color: COLORS.muted }}>
            Carga los links reales por recibo (vía ms-payment) y elige cuál va
            en el email.
          </span>
        </div>
      ) : (
        <>
          {links.map((link, i) => (
            <div
              key={link.invoiceId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.55rem",
                flexWrap: "wrap",
              }}
            >
              <span style={{ minWidth: "7.5rem" }}>
                Recibo {i + 1} de {links.length}
                {link.amountDueCents !== null && (
                  <strong>
                    {" "}
                    · {(link.amountDueCents / 100).toLocaleString("fr-FR", {
                      minimumFractionDigits: 2,
                    })}{" "}
                    €
                  </strong>
                )}
              </span>
              {i === 0 && (
                <Badge bg="#d1e7dd" fg="#0f5132">
                  Recomendado · más antiguo
                </Badge>
              )}
              {link.paymentUrl ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(link.paymentUrl!)}
                  style={secondaryButtonStyle}
                >
                  Usar este
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={disabled || creating === link.invoiceId}
                    onClick={() => createLink(link.invoiceId)}
                    style={secondaryButtonStyle}
                    title="Crea el link en Stripe vía ms-payment (idempotente: repetir no duplica). Queda auditado con tu usuario."
                  >
                    {creating === link.invoiceId ? "Creando…" : "Crear link"}
                  </button>
                  <span style={{ color: COLORS.muted }}>
                    sin link aún{link.error ? ` (${link.error})` : ""}
                  </span>
                </>
              )}
            </div>
          ))}
          <div style={{ color: COLORS.muted }}>
            <button type="button" disabled style={{ ...secondaryButtonStyle, opacity: 0.5, cursor: "not-allowed" }}>
              Un solo link por el TOTAL
            </button>{" "}
            en construcción (ms-payment P2.b) — mientras, elige un recibo o pega
            un link en el campo de arriba.
          </div>
        </>
      )}
      {error && <div style={{ color: "#b32424" }}>{error}</div>}
    </div>
  );
}

/**
 * La previsualización que ofrece el back (`wouldSend` del `dryRun`): plantilla,
 * destinatario, remitente, bcc y TODAS las variables finales ya fusionadas. Es
 * lo mismo que se devuelve en el envío real, así que lo que se ve aquí es
 * exactamente lo que sale.
 */
function PreviewPanel({ response }: { response: TaskSendResponse }) {
  const p = response.wouldSend;
  return (
    <section
      style={{
        marginBottom: "0.9rem",
        border: `1px solid ${COLORS.border}`,
        borderRadius: "0.5rem",
        padding: "0.75rem",
        background: COLORS.subtle,
      }}
    >
      <h3 style={sectionTitleStyle}>Previsualización del email</h3>
      <div style={{ fontSize: ".8rem", display: "grid", gap: "0.25rem" }}>
        <div>
          <strong>Plantilla:</strong> <code>{p.templateId}</code> ({p.market})
        </div>
        <div>
          <strong>Para:</strong> {p.to ?? "— (episodio sin destinatario)"}
        </div>
        <div>
          <strong>De:</strong> {p.sender.from}
          {p.sender.replyTo ? ` · responder a ${p.sender.replyTo}` : ""}
        </div>
        {p.bcc.length > 0 && (
          <div>
            <strong>CCO:</strong> {p.bcc.join(", ")}
          </div>
        )}
      </div>
      <h4
        style={{
          ...sectionTitleStyle,
          margin: "0.7rem 0 0.35rem",
          fontSize: ".72rem",
        }}
      >
        Variables finales
      </h4>
      <dl style={{ margin: 0, display: "grid", gap: "0.2rem" }}>
        {Object.entries(p.variables).map(([name, value]) => (
          <div
            key={name}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(150px, 1fr) 2fr",
              gap: "0.5rem",
            }}
          >
            <dt style={{ ...labelStyle, margin: 0, fontFamily: "monospace" }}>
              {name}
            </dt>
            <dd
              style={{
                margin: 0,
                fontSize: ".8rem",
                color: COLORS.ink,
                wordBreak: "break-word",
              }}
            >
              {renderVariableValue(value)}
            </dd>
          </div>
        ))}
      </dl>
      <div style={{ marginTop: "0.5rem" }}>
        <CopyableValue label="eventId (deduplicación)" value={p.eventId} />
      </div>
    </section>
  );
}

/**
 * Resultado del envío. La distinción `simulated` es la importante: con el gate
 * del mercado cerrado ms-ops responde 200 sin haber enviado nada, y confundir
 * eso con un envío real es creer que se ha escrito al cliente cuando no.
 */
function SendResult({ result }: { result: TaskSendResponse }) {
  if (result.simulated) {
    return (
      <div
        style={{
          padding: "0.6rem 0.85rem",
          background: "#e7f1ff",
          color: "#0b4f9e",
          border: "1px solid #b8d8ff",
          borderRadius: "0.45rem",
          marginBottom: "0.7rem",
          fontSize: ".82rem",
          lineHeight: 1.5,
        }}
      >
        <strong>No se ha enviado nada (simulación).</strong>{" "}
        {result.simulatedReason ??
          "El gate del mercado está cerrado: el motor decide pero no envía."}{" "}
        La tarea sigue en la cola.
      </div>
    );
  }
  return (
    <div
      style={{
        padding: "0.6rem 0.85rem",
        background: "#d4edda",
        color: "#155724",
        borderRadius: "0.45rem",
        marginBottom: "0.7rem",
        fontSize: ".82rem",
      }}
    >
      <strong>Enviado.</strong> Pasando a la siguiente tarea…
    </div>
  );
}

/**
 * Descartar da el paso por RESUELTO (`completedStepIdsOf` cuenta las
 * descartadas desde 2026-08-10, para que el episodio no se congele). La
 * consecuencia hay que decirla: esa carta no se enviará nunca y la cadencia
 * saltará al siguiente escalón. Si el cliente solo necesita tiempo, lo suyo es
 * «Aplazar».
 */
function DismissBox({
  reason,
  onReason,
  onCancel,
  onConfirm,
  busy,
}: {
  reason: string;
  onReason: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const valid = reason.trim().length > 0;
  return (
    <div
      style={{
        marginTop: "0.7rem",
        padding: "0.75rem",
        border: `1px solid ${COLORS.border}`,
        borderRadius: "0.5rem",
        background: COLORS.subtle,
      }}
    >
      <label style={fieldStyle}>
        <span style={labelStyle}>Motivo del descarte (obligatorio)</span>
        <input
          autoFocus
          value={reason}
          onChange={(e) => onReason(e.target.value)}
          placeholder="p. ej. el cliente llamó: va a domiciliar de nuevo"
          style={{ ...inputStyle, width: "100%" }}
        />
        <span style={{ fontSize: ".72rem", color: COLORS.muted, lineHeight: 1.4 }}>
          Esta carta NO se enviará y la cadencia continuará al siguiente escalón: el paso queda por resuelto. Si el cliente solo necesita tiempo, usa «Aplazar» en vez de descartar. Queda registrado con tu usuario — estas decisiones tienen consecuencias jurídicas y alguien las revisará meses después</span>
      </label>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.6rem" }}>
        <button
          type="button"
          style={{
            ...buttonStyle,
            background: COLORS.dangerFg,
            borderColor: COLORS.dangerFg,
            opacity: valid && !busy ? 1 : 0.5,
            cursor: valid && !busy ? "pointer" : "not-allowed",
          }}
          disabled={!valid || busy}
          onClick={onConfirm}
        >
          Confirmar descarte
        </button>
        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={onCancel}
          disabled={busy}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
